import classNames from "classnames";
import { ReactElement, useCallback, useEffect, useId, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { ReactComponent as AntennaIcon } from "assets/images/common/antenna.svg";
import { ReactComponent as CertificateIcon } from "assets/images/common/document.svg";
import { ReactComponent as EditIcon } from "assets/images/common/edit.svg";
import { ReactComponent as HighBatteryIcon } from "assets/images/common/full-battery.svg";
import { ReactComponent as InfoIcon } from "assets/images/common/info.svg";
import { ReactComponent as LowBatteryIcon } from "assets/images/common/low-battery.svg";
import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as PlugIcon } from "assets/images/common/plug.svg";
import { ReactComponent as RecalibrateIcon } from "assets/images/common/reset.svg";
import { ReactComponent as TrashIcon } from "assets/images/common/trash.svg";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import Button from "components/Button";
import Popover from "components/Popover";
import SearchInput from "components/SearchInput";
import Select from "components/Select";
import Table from "components/Table";

import { AssetsApi, DevicesApi } from "services/http";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  createDeviceRequest,
  getDeviceListRequest,
  getDeviceStatisticsRequest,
  resetCalibrationState,
  resetCreateDeviceState,
  resetRemoveDeviceState,
  resetUpdateDeviceState,
  updateDeviceRequest,
} from "store/slices/deviceSlice";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";

import { AssetItem } from "entities/assets";
import { BaseOption, ModalType, UserRole } from "entities/common";
import { CreateDevicePayload, DeviceItem, UpdateDevicePayload } from "entities/devices";

import { DeviceFormValues } from "widgets/Modal/CreateDevice/Content";

import styles from "./style.module.scss";

const PAGE_SIZE = 12;
const RELATIVE_TIME_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const CALIBRATION_DUE_SOON_DAYS = 30;

const MODEL_OPTIONS: BaseOption[] = [
  { value: "", label: "All types" },
  { value: "AM103", label: "Milesight AM103" },
  { value: "AM104", label: "Milesight AM104" },
  { value: "AM308", label: "Milesight AM308" },
  { value: "AM319 O3", label: "Milesight AM319 O3" },
  { value: "AM319 HCHO-IR", label: "Milesight AM319 HCHO-IR" },
  { value: "AM319 HCHO-IR-W", label: "Milesight AM319 HCHO-IR-W" },
];

const STATUS_OPTIONS: BaseOption[] = [
  { value: "", label: "All statuses" },
  { value: "online", label: "Online" },
  { value: "offline", label: "Offline" },
  { value: "never_seen", label: "Never seen" },
];

const MODEL_LABEL_BY_VALUE: Record<string, string> = MODEL_OPTIONS.reduce(
  (acc, o) => (o.value ? { ...acc, [o.value]: o.label } : acc),
  {} as Record<string, string>,
);
const formatModel = (model: string) => MODEL_LABEL_BY_VALUE[model] ?? model;

type DeviceStatus = "Online" | "Offline" | "Never seen";

function deriveStatus(lastSeen: string | null, reportingIntervalSeconds: number): DeviceStatus {
  if (!lastSeen) return "Never seen";
  const ageMs = Date.now() - new Date(lastSeen).getTime();
  return ageMs <= reportingIntervalSeconds * 2 * 1000 ? "Online" : "Offline";
}

const pad = (n: number) => String(n).padStart(2, "0");
const formatDateOnly = (d: Date) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
const formatTimeOnly = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const formatAbsoluteDateTime = (d: Date) => `${formatDateOnly(d)} ${formatTimeOnly(d)}`;

function formatRelativeTime(diffMs: number): string {
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ago`;
}

const startOfDayMs = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

type CalibrationCell = { label: string; variant: "calibrated" | "due" | "overdue"; sub: string };

function deriveCalibration(device: DeviceItem): CalibrationCell | null {
  if (!device.next_calibration_due) return null;
  const due = new Date(device.next_calibration_due);
  if (Number.isNaN(due.getTime())) return null;

  const diffDays = Math.round((startOfDayMs(due) - startOfDayMs(new Date())) / DAY_MS);
  const dueText = formatDateOnly(due);

  if (diffDays < 0) {
    return { label: "Overdue", variant: "overdue", sub: `Was due: ${dueText}` };
  }
  if (diffDays <= CALIBRATION_DUE_SOON_DAYS) {
    const label = `Due in ${diffDays} ${diffDays === 1 ? "day" : "days"}`;
    return { label, variant: "due", sub: `Next due: ${dueText}` };
  }
  return { label: "Calibrated", variant: "calibrated", sub: `Next due: ${dueText}` };
}

const AssetDevicesTab = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const callbackKey = useId();

  const [asset, setAsset] = useState<AssetItem | null>(null);
  const [search, setSearch] = useState("");
  const [statusValue, setStatusValue] = useState("");
  const [modelValue, setModelValue] = useState("");
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 500);

  const {
    items,
    meta,
    isLoading,
    statistics,
    lastCreatedDevEui,
    lastUpdatedDevEui,
    lastRemovedDevEui,
    lastCalibratedDevEui,
  } = useAppSelector((s: RootStore) => s.devices);
  const authUser = useAppSelector((s: RootStore) => s.auth.user);
  const isAdmin = authUser?.role === UserRole.ADMINISTRATOR;

  const statisticsQuery = useMemo(
    () => (id ? QueryHelper.getQueryString({ [QUERY_PARAMS.ASSET_ID]: Number(id) }) : ""),
    [id],
  );

  useEffect(() => {
    if (!id) return;
    let active = true;
    AssetsApi.getCurrent(id)
      .then(({ data }) => {
        if (active) setAsset(data);
      })
      .catch(() => {
        if (active) setAsset(null);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const fetchList = useCallback(
    (silent = false) => {
      if (!id) return;
      dispatch(
        getDeviceListRequest(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.ASSET_ID]: Number(id),
            [QUERY_PARAMS.SEARCH]: debouncedSearch.trim() || undefined,
            [QUERY_PARAMS.STATUS]: statusValue || undefined,
            [QUERY_PARAMS.DEVICE_MODEL]: modelValue || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: PAGE_SIZE,
          }),
          silent ? { silent: true } : undefined,
        ),
      );
    },
    [dispatch, id, debouncedSearch, statusValue, modelValue, page],
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusValue, modelValue]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    dispatch(getDeviceStatisticsRequest(statisticsQuery));
  }, [dispatch, statisticsQuery]);

  const reload = useCallback(() => {
    fetchList(true);
    dispatch(getDeviceStatisticsRequest(statisticsQuery, { silent: true }));
  }, [fetchList, dispatch, statisticsQuery]);

  useRealtime(["devices"], reload, { assetId: Number(id) });

  const handleSaveDevice = useCallback(
    (raw?: unknown) => {
      const values = raw as DeviceFormValues;
      const payload: CreateDevicePayload = {
        dev_eui: values.devEui,
        app_key: values.appKey,
        device_profile_key: values.deviceProfileKey,
        portfolio_id: Number(values.portfolioId),
        asset_id: Number(values.assetId),
        installation_location: values.installationLocation || null,
        reporting_interval_seconds: Number(values.reportingInterval),
        last_calibration_at: values.lastCalibrationDate,
        calibration_interval_months: Number(values.calibrationInterval),
      };
      dispatch(createDeviceRequest({ payload, certificate: values.certificate }));
    },
    [dispatch],
  );

  const handleUpdateDevice = useCallback(
    (devEui: string, raw?: unknown) => {
      const values = raw as DeviceFormValues;
      const body: UpdateDevicePayload = {
        asset_id: Number(values.assetId),
        installation_location: values.installationLocation || null,
        reporting_interval_seconds: Number(values.reportingInterval),
        calibration_interval_months: Number(values.calibrationInterval),
      };
      dispatch(updateDeviceRequest({ devEui, body }));
    },
    [dispatch],
  );

  useEffect(() => {
    if (lastCreatedDevEui) {
      dispatch(hideModal());
      dispatch(resetCreateDeviceState());
      fetchList();
      dispatch(getDeviceStatisticsRequest(statisticsQuery));
    }
  }, [lastCreatedDevEui, dispatch, fetchList, statisticsQuery]);

  useEffect(() => {
    if (lastUpdatedDevEui) {
      dispatch(hideModal());
      dispatch(resetUpdateDeviceState());
      fetchList();
      dispatch(getDeviceStatisticsRequest(statisticsQuery));
    }
  }, [lastUpdatedDevEui, dispatch, fetchList, statisticsQuery]);

  useEffect(() => {
    if (lastRemovedDevEui) {
      dispatch(hideModal());
      dispatch(resetRemoveDeviceState());
      fetchList();
      dispatch(getDeviceStatisticsRequest(statisticsQuery));
    }
  }, [lastRemovedDevEui, dispatch, fetchList, statisticsQuery]);

  useEffect(() => {
    if (lastCalibratedDevEui) {
      dispatch(hideModal());
      dispatch(resetCalibrationState());
      fetchList();
      dispatch(getDeviceStatisticsRequest(statisticsQuery));
    }
  }, [lastCalibratedDevEui, dispatch, fetchList, statisticsQuery]);

  const handleAddDevice = useCallback(() => {
    setModalCallbacks(callbackKey, { onSave: handleSaveDevice, onUpdate: handleUpdateDevice });
    const initialData: Partial<DeviceFormValues> = asset
      ? {
          companyId: asset.company_id != null ? String(asset.company_id) : "",
          companyLabel: asset.company_name ?? "",
          portfolioId: String(asset.portfolio_id),
          portfolioLabel: asset.portfolio_name ?? "",
          assetId: String(asset.id),
          assetLabel: asset.name,
        }
      : {};
    dispatch(
      showModal({
        modalType: ModalType.CREATE_DEVICE,
        modalProps: { mode: "create", callbackKey, initialData },
      }),
    );
  }, [asset, callbackKey, dispatch, handleSaveDevice, handleUpdateDevice]);

  const handleEditDevice = useCallback(
    async (deviceId: number) => {
      const device = items.find((d) => d.id === deviceId);
      if (!device) return;

      setModalCallbacks(callbackKey, { onSave: handleSaveDevice, onUpdate: handleUpdateDevice });

      let appKey = "";
      try {
        const { data } = await DevicesApi.getCurrent(device.dev_eui);
        appKey = data.app_key ?? "";
      } catch {
        appKey = "";
      }

      const initialData: Partial<DeviceFormValues> = {
        companyId: device.company_id != null ? String(device.company_id) : "",
        companyLabel: device.company_name ?? "",
        portfolioId: device.portfolio_id != null ? String(device.portfolio_id) : "",
        portfolioLabel: device.portfolio_name ?? "",
        assetId: device.asset_id != null ? String(device.asset_id) : "",
        assetLabel: device.asset_name ?? "",
        installationLocation: device.installation_location ?? "",
        deviceProfileKey: device.device_profile_key,
        devEui: device.dev_eui,
        appKey,
        reportingInterval: device.reporting_interval_seconds ? String(device.reporting_interval_seconds) : "",
        calibrationInterval:
          device.calibration_interval_months != null ? String(device.calibration_interval_months) : "12",
      };

      dispatch(
        showModal({
          modalType: ModalType.CREATE_DEVICE,
          modalProps: { mode: "edit", id: deviceId, callbackKey, initialData },
        }),
      );
    },
    [callbackKey, dispatch, handleSaveDevice, handleUpdateDevice, items],
  );

  const handleRemoveDevice = useCallback(
    (deviceId: number) => {
      const device = items.find((d) => d.id === deviceId);
      if (!device) return;
      dispatch(
        showModal({
          modalType: ModalType.REMOVE_DEVICE,
          modalProps: { name: device.device_name, devEui: device.dev_eui },
        }),
      );
    },
    [dispatch, items],
  );

  const handleRecalibrate = useCallback(
    (deviceId: number) => {
      const device = items.find((d) => d.id === deviceId);
      if (!device) return;

      const toDisplayDate = (iso?: string | null) => {
        if (!iso) return undefined;
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? undefined : formatDateOnly(d);
      };

      dispatch(
        showModal({
          modalType: ModalType.MARK_CALIBRATED,
          modalProps: {
            devEui: device.dev_eui,
            deviceInfo: {
              model: device.device_model ? formatModel(device.device_model) : device.device_model,
              eui: device.dev_eui,
              lastCalibration: toDisplayDate(device.last_calibration_at),
              nextCalibrationDue: toDisplayDate(device.next_calibration_due),
            },
          },
        }),
      );
    },
    [dispatch, items],
  );

  const devicesInfo = useMemo(
    () => [
      {
        key: "total",
        title: "Total devices",
        value: statistics.total.toString(),
        icon: (
          <span className={classNames(styles.statIcon, styles["statIcon--gray"])}>
            <AntennaIcon />
          </span>
        ),
      },
      {
        key: "online",
        title: "Online",
        value: statistics.online.toString(),
        icon: (
          <span className={classNames(styles.statIcon, styles["statIcon--green"])}>
            <span className={styles.statDot} />
          </span>
        ),
      },
      {
        key: "offline",
        title: "Offline",
        value: statistics.offline.toString(),
        icon: (
          <span className={classNames(styles.statIcon, styles["statIcon--red"])}>
            <span className={styles.statDot} />
          </span>
        ),
      },
      {
        key: "never_seen",
        title: "Never seen",
        value: statistics.never_seen.toString(),
        icon: (
          <span className={classNames(styles.statIcon, styles["statIcon--orange"])}>
            <span className={styles.statDot} />
          </span>
        ),
      },
    ],
    [statistics],
  );

  const tableHeaders = useMemo(
    () => [
      { label: "Location", key: "location" },
      { label: "Device", key: "device" },
      { label: "Status", key: "status" },
      { label: "Battery", key: "battery" },
      { label: "Last update", key: "lastUpdate" },
      { label: "Calibration", key: "calibration" },
      { label: "Actions", key: "actions" },
    ],
    [],
  );

  const tableData = useMemo(
    () =>
      items.map((d: DeviceItem) => {
        const status = deriveStatus(d.last_seen_at, d.reporting_interval_seconds);
        const statusVariant = status === "Online" ? "online" : status === "Offline" ? "offline" : "neverSeen";

        const battery: ReactElement =
          d.battery_level == null || status !== "Online" ? (
            <span className={styles.muted}>—</span>
          ) : d.battery_level === 0 ? (
            <span className={classNames(styles.battery, styles.batteryMains)} title="Mains powered">
              <PlugIcon />
            </span>
          ) : (
            <span
              className={classNames(styles.battery, d.battery_level > 15 ? styles.batteryHigh : styles.batteryLow)}
            >
              {d.battery_level > 15 ? <HighBatteryIcon /> : <LowBatteryIcon />}
              <span className={styles.batteryText}>{d.battery_level}%</span>
            </span>
          );

        const lastSeen = d.last_seen_at ? new Date(d.last_seen_at) : null;
        const validLastSeen = lastSeen && !Number.isNaN(lastSeen.getTime()) ? lastSeen : null;
        const diffMs = validLastSeen ? Date.now() - validLastSeen.getTime() : 0;

        let lastUpdate: ReactElement;
        if (!validLastSeen) {
          lastUpdate = <span className={styles.muted}>—</span>;
        } else if (diffMs < RELATIVE_TIME_THRESHOLD_MS) {
          lastUpdate = (
            <span className={styles.lastUpdate}>
              <span>{formatRelativeTime(diffMs)}</span>
              <Popover
                id={`asset-device-last-seen-${d.id}`}
                variant="hover"
                positionY="middle"
                positionX="content-end"
                content={formatAbsoluteDateTime(validLastSeen)}
                popoverClassName={styles.lastUpdatePopover}
              >
                <span className={styles.lastUpdateInfo}>
                  <InfoIcon />
                </span>
              </Popover>
            </span>
          );
        } else {
          lastUpdate = <span className={styles.lastUpdateAbsolute}>{formatAbsoluteDateTime(validLastSeen)}</span>;
        }

        const calibration = deriveCalibration(d);
        const calibrationCell: ReactElement = !calibration ? (
          <span className={styles.muted}>—</span>
        ) : (
          <span className={styles.calibration}>
            <span className={styles.calibrationHead}>
              <span className={classNames(styles.calibrationStatus, styles[calibration.variant])}>
                {calibration.label}
              </span>
              {calibration.variant === "calibrated" && d.has_calibration_certificate && d.certificate_url && (
                <a
                  className={styles.certificateIcon}
                  href={d.certificate_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open calibration certificate"
                >
                  <CertificateIcon />
                </a>
              )}
            </span>
            <span className={styles.calibrationSub}>{calibration.sub}</span>
          </span>
        );

        const canManage =
          isAdmin || d.asset_manager_id === authUser?.id || d.portfolio_manager_id === authUser?.id;

        return {
          id: d.id,
          location: <span className={styles.location}>{d.installation_location || "—"}</span>,
          device: (
            <span className={styles.device}>
              <span className={styles.deviceName}>{d.device_model ? formatModel(d.device_model) : "—"}</span>
              <span className={styles.deviceType}>{d.dev_eui}</span>
            </span>
          ),
          status: <span className={classNames(styles.status, styles[statusVariant])}>{status}</span>,
          battery,
          lastUpdate,
          calibration: calibrationCell,
          actions: (
            <div className="table-actions">
              {canManage && (
                <Button
                  variant="link"
                  aria-label="Edit device"
                  className="table-actions__button"
                  onClick={() => handleEditDevice(d.id)}
                >
                  <EditIcon />
                </Button>
              )}
              <Button
                variant="link"
                aria-label="Recalibrate device"
                className="table-actions__button"
                onClick={() => handleRecalibrate(d.id)}
              >
                <RecalibrateIcon />
              </Button>
              {canManage && (
                <Button
                  variant="link"
                  aria-label="Delete device"
                  className="table-actions__button"
                  onClick={() => handleRemoveDevice(d.id)}
                >
                  <TrashIcon />
                </Button>
              )}
            </div>
          ),
        };
      }),
    [items, handleEditDevice, handleRemoveDevice, handleRecalibrate, isAdmin, authUser?.id],
  );

  const totalPages = useMemo(() => Math.max(1, Math.ceil(meta.total / PAGE_SIZE)), [meta.total]);

  return (
    <div className={styles.list}>
      <div className={styles.info}>
        {devicesInfo.map((item) => (
          <div className={styles.infoItem} key={item.key}>
            <div className={styles.infoItemContent}>
              <p className={styles.infoItemValue}>{item.value}</p>
              <p className={styles.infoItemTitle}>{item.title}</p>
            </div>
            {item.icon}
          </div>
        ))}
      </div>

      <div className={styles.toolbar}>
        <SearchInput
          name="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles["search-input"]}
          placeholder="Search devices..."
        />

        <div className={styles.filters}>
          <Select
            variant="secondary"
            options={MODEL_OPTIONS}
            value={modelValue}
            onChange={setModelValue}
            placeholder="All types"
            className={styles.select}
          />

          <Select
            variant="secondary"
            options={STATUS_OPTIONS}
            value={statusValue}
            onChange={setStatusValue}
            placeholder="All statuses"
            className={styles.select}
          />
        </div>

        <Button variant="primary" className={styles["header-create"]} onClick={handleAddDevice}>
          Add device
        </Button>
      </div>

      {!isLoading && tableData.length === 0 && (
        <div className={styles["not-found"]}>
          <NotFoundIcon />
          <p>No devices found</p>
        </div>
      )}

      {tableData.length > 0 && (
        <div className={styles["table-scroll"]}>
          <Table
            headers={tableHeaders}
            data={tableData}
            pagination={{ total: totalPages, page, onPageChange: setPage }}
            className={styles.table}
          />
        </div>
      )}
    </div>
  );
};

export default AssetDevicesTab;
